import {
  createParser,
  createSerializer,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs'
import type { Values } from 'nuqs'
import type { components } from '@joyful/openapi/types.gen'
import { PracticeRequestTypeSchema } from '../../../../core/components/practice-request/PracticeRequestTypeTag'
import { cleanupStaleFacetValues } from '../../filtering/filterBuilders'
import { createEnumSchema, parseAsUuid } from '@joyful/utils'

const PracticeRequestListStatusOrder = ['DRAFT', 'OPEN', 'CLOSED'] as const

export type PracticeRequestListStatus =
  (typeof PracticeRequestListStatusOrder)[number]

type PracticeRequestType = components['schemas']['PracticeRequestType']

export const PracticeRequestListSortKeySchema = createEnumSchema<
  components['schemas']['PracticeRequestListSortKey']
>([
  { title: 'Status', value: 'status' },
  { title: 'Type', value: 'request_type' },
  { title: 'Outstanding', value: 'outstanding_charged_amount' },
  { title: 'Visibility', value: 'visibility' },
  { title: 'Created', value: 'created_at' },
  { title: 'Last activity', value: 'last_activity_at' },
  { title: 'Expires', value: 'expiration_date' },
])

function parseAsClampedInteger({
  defaultValue,
  maximumValue,
}: {
  defaultValue: number
  maximumValue?: number
}) {
  return createParser({
    parse: (value) => {
      const parsed = parseAsInteger.parse(value)
      if (parsed === null) {
        return null
      }

      const lowerBoundedValue = Math.max(parsed, 1)
      return maximumValue
        ? Math.min(lowerBoundedValue, maximumValue)
        : lowerBoundedValue
    },
    serialize: (value) => {
      return String(value)
    },
  }).withDefault(defaultValue)
}

export const PracticeRequestsListQueryMap = {
  assigned_to_me: parseAsBoolean.withDefault(false),
  include_closed: parseAsBoolean.withDefault(false),
  limit: parseAsClampedInteger({ defaultValue: 50, maximumValue: 100 }),
  needs_response: parseAsBoolean.withDefault(false),
  page: parseAsClampedInteger({ defaultValue: 1 }),
  patient_ids: parseAsArrayOf(parseAsUuid).withDefault([]),
  publisher_user_ids: parseAsArrayOf(parseAsUuid).withDefault([]),
  query: parseAsString.withDefault(''),
  request_type: parseAsArrayOf(
    parseAsStringLiteral(PracticeRequestTypeSchema.values()),
  ).withDefault([]),
  snoozed: parseAsBoolean.withDefault(false),
  sort_key: parseAsStringLiteral(PracticeRequestListSortKeySchema.values()),
  sort_order: parseAsStringLiteral(['asc', 'desc'] as const).withDefault(
    'desc',
  ),
  status: parseAsArrayOf(
    parseAsStringLiteral(PracticeRequestListStatusOrder),
  ).withDefault([]),
}

const PracticeRequestsOverlayQueryMap = {
  create: parseAsBoolean.withDefault(false),
  settings: parseAsBoolean.withDefault(false),
}

export const PracticeRequestsRouteQueryMap = {
  ...PracticeRequestsListQueryMap,
  ...PracticeRequestsOverlayQueryMap,
}

export type PracticeRequestsRouteQueryParams = Values<
  typeof PracticeRequestsRouteQueryMap
>

type PracticeRequestPatientId =
  PracticeRequestsRouteQueryParams['patient_ids'][number]

type PracticeRequestPublisherUserId =
  PracticeRequestsRouteQueryParams['publisher_user_ids'][number]

export const EmptyPracticeRequestsListParams: PracticeRequestsRouteQueryParams =
  {
    assigned_to_me: false,
    create: false,
    include_closed: false,
    limit: 50,
    needs_response: false,
    page: 1,
    patient_ids: [],
    publisher_user_ids: [],
    query: '',
    request_type: [],
    settings: false,
    snoozed: false,
    sort_key: null,
    sort_order: 'desc',
    status: [],
  }

export type PracticeRequestListDraftListing =
  | 'hidden'
  | 'on_filter'
  | 'with_drafts'

function defaultPracticeRequestListStatuses(
  draftListing: PracticeRequestListDraftListing,
): Array<PracticeRequestListStatus> {
  return draftListing === 'with_drafts' ? ['DRAFT', 'OPEN'] : ['OPEN']
}

/** Adds or removes one value, keeping the result in the canonical filter order. */
function toggleOrderedFilterValue<Value>({
  order,
  selectedValues,
  value,
}: {
  order: ReadonlyArray<Value>
  selectedValues: ReadonlyArray<Value>
  value: Value
}): Array<Value> {
  const nextValues = selectedValues.includes(value)
    ? selectedValues.filter((selectedValue) => {
        return selectedValue !== value
      })
    : [...selectedValues, value]
  return order.filter((orderedValue) => {
    return nextValues.includes(orderedValue)
  })
}

/** Statuses the list should show for the current URL and viewer. */
export function resolveEffectivePracticeRequestListStatuses({
  draftListing,
  requestedStatuses,
}: {
  draftListing: PracticeRequestListDraftListing
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
}): Array<PracticeRequestListStatus> {
  const listableStatuses = PracticeRequestListStatusOrder.filter((status) => {
    const isHiddenDraft = status === 'DRAFT' && draftListing === 'hidden'
    return requestedStatuses.includes(status) && !isHiddenDraft
  })
  if (listableStatuses.length > 0) {
    return listableStatuses
  }

  return defaultPracticeRequestListStatuses(draftListing)
}

/** True when the list is scoped to drafts only via the Drafts quick filter. */
export function isPracticeRequestListDraftsFilterActive({
  draftListing,
  needsResponse,
  requestedStatuses,
  snoozed,
}: {
  draftListing: PracticeRequestListDraftListing
  needsResponse: boolean
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
  snoozed: boolean
}): boolean {
  if (draftListing === 'hidden' || needsResponse || snoozed) {
    return false
  }

  return requestedStatuses.length === 1 && requestedStatuses.at(0) === 'DRAFT'
}

/** Keep a non-default status selection in the URL, otherwise clear it. */
export function normalizePracticeRequestListStatusFilter({
  draftListing,
  statuses,
}: {
  draftListing: PracticeRequestListDraftListing
  statuses: ReadonlyArray<PracticeRequestListStatus>
}): Array<PracticeRequestListStatus> {
  const orderedStatuses = PracticeRequestListStatusOrder.filter((status) => {
    return statuses.includes(status)
  })
  const defaultStatuses = defaultPracticeRequestListStatuses(draftListing)
  const isDefaultSelection = orderedStatuses.join() === defaultStatuses.join()
  return isDefaultSelection ? [] : orderedStatuses
}

/** Add or remove one request type from the current list filter. */
export function togglePracticeRequestListTypeFilter({
  requestType,
  requestedTypes,
}: {
  requestType: PracticeRequestType
  requestedTypes: ReadonlyArray<PracticeRequestType>
}): Array<PracticeRequestType> {
  return toggleOrderedFilterValue({
    order: PracticeRequestTypeSchema.values(),
    selectedValues: requestedTypes,
    value: requestType,
  })
}

/** Add or remove one status from the current list filter. */
export function togglePracticeRequestListStatusFilter({
  draftListing,
  requestedStatuses,
  status,
}: {
  draftListing: PracticeRequestListDraftListing
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
  status: PracticeRequestListStatus
}): Array<PracticeRequestListStatus> {
  const nextStatuses = toggleOrderedFilterValue({
    order: PracticeRequestListStatusOrder,
    selectedValues: resolveEffectivePracticeRequestListStatuses({
      draftListing,
      requestedStatuses,
    }),
    value: status,
  })
  return normalizePracticeRequestListStatusFilter({
    draftListing,
    statuses: nextStatuses,
  })
}

/** List and facet query fields for the selected statuses. */
export function practiceRequestListStatusQueryParams({
  draftListing,
  includeClosed = false,
  requestedStatuses,
}: {
  draftListing: PracticeRequestListDraftListing
  includeClosed?: boolean
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
}) {
  if (requestedStatuses.length > 0) {
    return { statuses: [...requestedStatuses] }
  }

  if (draftListing === 'on_filter') {
    const statuses: Array<PracticeRequestListStatus> = includeClosed
      ? ['OPEN', 'CLOSED']
      : ['OPEN']
    return { statuses }
  }

  return { include_closed: includeClosed }
}

export interface PracticeRequestFacetValuesState {
  isAuthorizedBaseScopeResponse: boolean
  isPlaceholderData: boolean
  patientIds: ReadonlyArray<PracticeRequestPatientId>
  publisherUserIds: ReadonlyArray<PracticeRequestPublisherUserId>
  requestTypes: ReadonlyArray<PracticeRequestType>
  status: 'error' | 'pending' | 'ready'
}

/**
 * Drops type and patient filters a settled facet response proves unavailable.
 *
 * Returns the same `queryParams` reference when nothing is stale, so callers can
 * compare by identity to decide whether the URL needs replacing.
 */
export function cleanupStalePracticeRequestFacetFilters({
  facetValuesState,
  queryParams,
}: {
  facetValuesState: PracticeRequestFacetValuesState
  queryParams: PracticeRequestsRouteQueryParams
}): PracticeRequestsRouteQueryParams {
  const { isAuthorizedBaseScopeResponse, isPlaceholderData, status } =
    facetValuesState
  const cleanedRequestTypes = cleanupStaleFacetValues({
    facetValuesState: {
      isAuthorizedBaseScopeResponse,
      isPlaceholderData,
      status,
      values: facetValuesState.requestTypes,
    },
    selectedValues: queryParams.request_type,
  })
  const cleanedPatientIds = cleanupStaleFacetValues({
    facetValuesState: {
      isAuthorizedBaseScopeResponse,
      isPlaceholderData,
      status,
      values: facetValuesState.patientIds,
    },
    selectedValues: queryParams.patient_ids,
  })
  const cleanedPublisherUserIds = cleanupStaleFacetValues({
    facetValuesState: {
      isAuthorizedBaseScopeResponse,
      isPlaceholderData,
      status,
      values: facetValuesState.publisherUserIds,
    },
    selectedValues: queryParams.publisher_user_ids,
  })
  const isUnchanged =
    cleanedRequestTypes.length === queryParams.request_type.length &&
    cleanedPatientIds.length === queryParams.patient_ids.length &&
    cleanedPublisherUserIds.length === queryParams.publisher_user_ids.length
  if (isUnchanged) {
    return queryParams
  }

  return {
    ...queryParams,
    page: EmptyPracticeRequestsListParams.page,
    patient_ids: cleanedPatientIds,
    publisher_user_ids: cleanedPublisherUserIds,
    request_type: cleanedRequestTypes,
  }
}

export function buildPracticeRequestsListRequestQuery({
  currentUserId,
  draftListing,
  isPracticeAssigneeEnabled,
  queryParams,
}: {
  currentUserId: string
  draftListing: PracticeRequestListDraftListing
  isPracticeAssigneeEnabled: boolean
  queryParams: PracticeRequestsRouteQueryParams
}) {
  return {
    ...practiceRequestListStatusQueryParams({
      draftListing,
      includeClosed: queryParams.include_closed,
      requestedStatuses: queryParams.status,
    }),
    limit: Math.max(1, queryParams.limit),
    page: Math.max(1, queryParams.page),
    patient_ids:
      queryParams.patient_ids.length > 0 ? queryParams.patient_ids : undefined,
    pending_response: queryParams.needs_response || undefined,
    practice_assignee_user_id:
      isPracticeAssigneeEnabled && queryParams.assigned_to_me
        ? currentUserId
        : undefined,
    publisher_user_ids:
      queryParams.publisher_user_ids.length > 0
        ? queryParams.publisher_user_ids
        : undefined,
    query: queryParams.query.trim() || undefined,
    request_types:
      queryParams.request_type.length > 0
        ? queryParams.request_type
        : undefined,
    snoozed: queryParams.snoozed || undefined,
    sort_key: queryParams.sort_key ?? undefined,
    sort_order: queryParams.sort_order,
  }
}

export function usePracticeRequestsRouteQueryParams() {
  return useQueryStates(PracticeRequestsRouteQueryMap, { history: 'push' })
}

export const serializePracticeRequestsRouteQueryParams = createSerializer(
  PracticeRequestsRouteQueryMap,
)
